import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import { ThemeProvider } from 'zengenti-forms-package/theme/themeContext';
import { defaultTheme } from 'zengenti-forms-package/theme/form';
import {
  makeSelectFormStatus,
  makeSelectPagedFields,
  makeSelectPagingInfo,
  makeSelectDefaultLang,
  makeSelectFormSettings,
  makeSelectFormEntries,
  makeSelectFormPostData,
  makeSelectFormFieldErrors,
} from 'zengenti-forms-package/redux/selectors';
import { 
  setDateRangeValues,
  setFormId,
  setValue,
  onSubmit,
  doTogglePageBack,
  doTogglePageForward,
  onValidateField
} from 'zengenti-forms-package/redux/actions';
import FormStyled from './form.styled';
import FormComposer from '../composer/formComposer';
import { Loader } from 'zengenti-forms-package/components/loader';
import { Button } from 'zengenti-forms-package/components/button';
import { Callout } from 'zengenti-forms-package/components/callout';


const Form = ({
  className,
  formId,
  useDefaultTheme = true,
  onCustomSubmit,
}) => {
  const dispatch = useDispatch();

  const _setFormId = (formId) => dispatch(setFormId(formId));
  const _setValue = (formId, id, value) => dispatch(setValue(formId, id, value));
  const _setDateRangeValues = (formId, id, dateType, value) => dispatch(setDateRangeValues(formId, id, dateType, value));
  const _onValidateField = (formId, id, value) => dispatch(onValidateField(formId, id, value));
  const _doTogglePageForward = (formId, pageIndex) => dispatch(doTogglePageForward(formId, pageIndex));
  const _doTogglePageBack = (formId, pageIndex) => dispatch(doTogglePageBack(formId, pageIndex)); 
  const _onSubmit = formId => dispatch(onSubmit(formId));

  useEffect(() => {
    if (formId) _setFormId(formId)
  }, [formId]); 

  const selectFormFields = makeSelectPagedFields(formId);
  const selectFormStatus = makeSelectFormStatus(formId);
  const selectPagingInfo = makeSelectPagingInfo(formId);
  const selectDefaultLang = makeSelectDefaultLang(formId);
  const selectFormSettings = makeSelectFormSettings(formId);
  const selectFormFieldErrors = makeSelectFormFieldErrors(formId)
  const selectFormEntries = makeSelectFormEntries(formId);
  const selectFormPostData = makeSelectFormPostData(formId);

  const status = useSelector(selectFormStatus);
  const fields = useSelector(selectFormFields);
  const pagingInfo = useSelector(selectPagingInfo);
  const defaultLanguage = useSelector(selectDefaultLang);
  const settings = useSelector(selectFormSettings);
  const errors = useSelector(selectFormFieldErrors);
  const entries = useSelector(selectFormEntries);
  const formData = useSelector(selectFormPostData);

  const currentGroup = pagingInfo?.currentPageId;

  if (pagingInfo && pagingInfo.pageCount > 1) {
    const isLastPage = pagingInfo.pageCount == pagingInfo.pageIndex + 1;
    return (
      <ThemeProvider theme={defaultTheme}>
      <FormStyled
        className={className}
        id={formId}
        useDefaultTheme={useDefaultTheme}
        >
          {!status?.hasSuccess && !status?.isLoading && (
            <>
              
              <FormComposer
                fields={fields}
                formData={formData}
                formId={formId}
                setValue={_setValue}
                onValidateField={_onValidateField}
                defaultLanguage={defaultLanguage}
                errors={errors}
                pagingInfo={pagingInfo}
                useDefaultTheme={useDefaultTheme}
                entries={entries}
                setDateRangeValues={_setDateRangeValues}
              />
              <div className='form__btns'>
              {pagingInfo.pageIndex > 0 && (
                <Button
                  className="form__btn--prev"
                  type="button"
                  text="Go Back"
                  action={() =>
                    _doTogglePageBack(formId, pagingInfo.pageIndex - 1)
                  }
                  useDefaultTheme={useDefaultTheme}
                />
              )}
              {!isLastPage && (
                <Button
                  className="form__btn--next"
                  type="button"
                  text="Next"
                  action={() =>
                    _doTogglePageForward(formId, pagingInfo.pageIndex + 1)
                  }
                  useDefaultTheme={useDefaultTheme}
                />
              )}
              {isLastPage && (
                <Button
                  className="form__btn--submit"
                  text={settings?.submitButtonText || "Submit"}
                  type="button"
                  loading={status?.isSubmitting}
                  action={() => {
                    _onSubmit(formId);
                    if (onCustomSubmit) onCustomSubmit();
                  }}
                  useDefaultTheme={useDefaultTheme}
                />
                )}
                </div>
            </>
          )}
          {status?.isLoading && !status?.hasSuccess && <Loader className="loading" height={24} width={24} color="#333" />}
          {status?.hasSuccess && status?.messages.success && (
          <p className="success-message">{status.messages.success}</p>
          )}
          {errors?.length >= 1 && (
            <div className="form__errors" role="alert">
              {errors?.map(({ message, groupId }, i) => {
                if (currentGroup === groupId) {
                  return <Callout className='form__error' text={message} key={i} />
                }
              })}
            </div>
          )}
        </FormStyled>
        </ThemeProvider>
    )
  } else {
    return (
      <ThemeProvider theme={defaultTheme}>
      <FormStyled
        className={className}
        id={formId}
        useDefaultTheme={useDefaultTheme}
      >
        {!status?.hasSuccess && !status?.isLoading && (
              <>
                <FormComposer
                  fields={fields}
                  formId={formId}
                  setValue={_setValue}
                  setDateRangeValues={_setDateRangeValues}
                  onValidateField={_onValidateField}
                  defaultLanguage={defaultLanguage}
                  errors={errors}
                  useDefaultTheme={useDefaultTheme}
                  entries={entries}
              />
              <div className='form__btns'>
              <Button
                  className="form__btn--submit"
                  loading={status?.isSubmitting}
                  text={settings?.submitButtonText || "Submit"}
                  type="button"
                  action={() => {
                    _onSubmit(formId);
                    if (onCustomSubmit) onCustomSubmit();
                  }}
                  useDefaultTheme={useDefaultTheme}
                />
                </div>
            </>
          )}
          {status?.isLoading && !status?.hasSuccess && <Loader className="loading" height={24} width={24} color="#333" />}
          {status?.hasSuccess && status?.messages.success && (
          <p className="success-message">{status.messages.success}</p>
          )}
          {errors?.length >= 1 && (
            <div className="form__errors"  role="alert">
              {errors?.map(({ message }, i) => {
                return <Callout className='form__error' text={message} key={i} />
              })}
            </div>
          )}  
        </FormStyled>
        </ThemeProvider>
    );
  }
};

Form.propTypes = {
  className: PropTypes.string,
  formId: PropTypes.string,
  useDefaultTheme: PropTypes.bool,
  onCustomSubmit: PropTypes.func,
};

export default Form;
